import React, { useEffect, useRef, useState } from "react";
import { IconVolumeOff, IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

export interface MVideoPlayerProps {
  videoUrl: string;
  open: boolean;
  onClose: () => void;
  title?: string;
  autoplay?: boolean;
}

const isYouTubeShorts = (url: string): boolean =>
  /youtube\.com\/shorts\//.test(url);

const getYouTubeEmbedUrl = (url: string, autoplay: boolean): string | null => {
  const match = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|shorts\/|watch\?v=|watch\?.+&v=))([\w-]{11})/
  );
  if (!match) return null;

  const base = `https://www.youtube.com/embed/${match[1]}`;
  if (!autoplay) return base;
  return `${base}?autoplay=1&mute=1&playsinline=1`;
};

const MVideoPlayer: React.FC<MVideoPlayerProps> = ({
  videoUrl,
  open,
  onClose,
  autoplay = false,
}) => {
  const { t } = useTranslation();
  const youtubeEmbed = getYouTubeEmbedUrl(videoUrl, autoplay);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(autoplay);
  // YouTube embeds can't expose their real dimensions cross-origin, so fall
  // back to the Shorts URL pattern there; native <video> measures its own
  // loaded metadata so the box always matches the actual clip's orientation.
  const [isPortrait, setIsPortrait] = useState(isYouTubeShorts(videoUrl));

  const handleUnmute = () => {
    const videoEl = videoRef.current;
    if (videoEl) videoEl.muted = false;
    setIsMuted(false);
  };

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open || youtubeEmbed) return;
    const videoEl = videoRef.current;
    if (!videoEl) return;

    // Some mobile browsers ignore the autoPlay attribute on initial mount —
    // force muted playback explicitly to guarantee it starts.
    if (autoplay) {
      videoEl.muted = true;
      videoEl.play().catch(() => {});
    }

    // iOS plays inline video in its own native fullscreen view when the
    // user taps the expand control. Treat leaving that native view the
    // same as tapping our close button, instead of stranding the user on
    // the orphaned player underneath it.
    const handleNativeFullscreenExit = () => onClose();
    videoEl.addEventListener("webkitendfullscreen", handleNativeFullscreenExit);
    return () => {
      videoEl.removeEventListener(
        "webkitendfullscreen",
        handleNativeFullscreenExit
      );
    };
  }, [open, autoplay, youtubeEmbed, videoUrl, onClose]);

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Close button — inset from safe area so it clears the notch/status bar */}
      <button
        onClick={onClose}
        aria-label="Close"
        style={{
          position: "absolute",
          top: "calc(16px + env(safe-area-inset-top, 0px))",
          left: "calc(16px + env(safe-area-inset-left, 0px))",
          zIndex: 1,
          background: "rgba(0,0,0,0.6)",
          border: "none",
          borderRadius: "50%",
          width: 44,
          height: 44,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          touchAction: "manipulation",
        }}
      >
        <IconX size={22} color="#fff" />
      </button>

      {!youtubeEmbed && isMuted && (
        <button
          onClick={handleUnmute}
          className="absolute bottom-[calc(24px+env(safe-area-inset-bottom))] left-1/2 z-[1] flex -translate-x-1/2 touch-manipulation items-center gap-2 rounded-full bg-black/60 px-4 py-2 text-white"
        >
          <IconVolumeOff size={20} />
          <span className="text-sm">{t("auth.exploreScreen.tapForSound")}</span>
        </button>
      )}

      <div
        style={{
          width: isPortrait ? "min(100vw, calc(100vh * 9 / 16))" : "100vw",
          height: isPortrait ? "100vh" : "min(100vh, calc(100vw * 9 / 16))",
          position: "relative",
        }}
      >
        {youtubeEmbed ? (
          <iframe
            src={youtubeEmbed}
            title="video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
            }}
          />
        ) : (
          <video
            ref={videoRef}
            src={videoUrl}
            controls
            autoPlay={autoplay}
            muted={autoplay}
            playsInline
            onVolumeChange={(e) => setIsMuted(e.currentTarget.muted)}
            onLoadedMetadata={(e) => {
              const v = e.currentTarget;
              if (v.videoWidth && v.videoHeight) {
                setIsPortrait(v.videoHeight >= v.videoWidth);
              }
            }}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
            }}
          />
        )}
      </div>
    </div>
  );
};

export default MVideoPlayer;
